// @flow

import * as React from "react";
import useAsyncState from "../hooks/useAsyncState";
import PubSub from "../modules/util/pubsub";
import _ from "lodash";
import { getAutoSuggestions } from "../api/search";

const { useState, useEffect, useRef } = React;

type PropsType = {
    standalone?: boolean,
    searchTerm?: string,
    action: string,
    parameter: string,
};

const API = {
    search: (query: string): Promise<any> => {
        return getAutoSuggestions(query);
    },
};

const decodeHTML = (html: string): string => {
    var txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
};

const QuickSearchResult = (props: {
    query: string,
    text: string,
    noLine: boolean,
}): React.Node => {
    const decodedText = props.text.indexOf("&")
        ? decodeHTML(props.text)
        : props.text;
    const indexOfMatch = props.query
        ? decodedText.toLowerCase().indexOf(props.query.toLowerCase())
        : -1;

    let before = decodedText;
    let inside = "";
    let after = "";

    if (indexOfMatch !== -1) {
        before = decodedText.substr(0, indexOfMatch);
        inside = decodedText.substr(indexOfMatch, props.query.length);
        after = decodedText.substr(indexOfMatch + props.query.length);
    }

    return (
        <span>
            {before}
            <strong>{inside}</strong>
            {after}
        </span>
    );
};

QuickSearchResult.defaultProps = {
    noLine: false,
    query: "",
};

const QuickSearch = (props: PropsType): React.Node => {
    const [results, setResults] = useState([]);
    const [isFocused, setIsFocussed] = useState(false);
    const [state, actions] = useAsyncState();
    const [inputValue, setInputValue] = useState(props.searchTerm || "");
    const [timer, setTimer] = useState(null);

    const search = (searchValue: string) => {
        actions.request();
        API.search(searchValue)
            .then((response: string[]) => {
                setResults(response);
                actions.success();
            })
            .catch((error: any) => {
                actions.fail("Something went wrong");
            });
    };

    const debouncedSearch = useRef(_.debounce(search, 250)).current;

    useEffect(() => {
        if (
            inputValue.length >= 3 &&
            debouncedSearch &&
            inputValue !== props.searchTerm &&
            isFocused
        ) {
            debouncedSearch(inputValue);
            if (props.standalone) {
                showOverlay();
            }
        } else if (inputValue.length === 0) {
            setResults([]);
            hideOverlay();
        }

        if (props.standalone) {
            if (isFocused && results.length) {
                showOverlay();
            } else {
                hideOverlay();
            }
        }
    }, [inputValue, debouncedSearch, isFocused]);

    const handleChange = (event: SyntheticEvent<HTMLInputElement>) => {
        setInputValue(event.currentTarget.value);
    };

    const handleClear = () => {
        setInputValue("");
    };

    const showOverlay = () => {
        PubSub.publish("show-content-overlay");
    };

    const hideOverlay = () => {
        PubSub.publish("hide-content-overlay");
    };

    const queueBlur = () => {
        setTimer(
            setTimeout(() => {
                setIsFocussed(false);
            }, 100)
        );
    };

    const queueFocus = () => {
        setIsFocussed(true);
        if (timer) {
            clearTimeout(timer);
        }
    };

    return (
        <div
            className={
                props.standalone
                    ? "quicksearch--standalone"
                    : "quicksearch--inline"
            }
        >
            <form action={props.action} method="GET">
                <div
                    className={`quicksearch__inner ${
                        isFocused ? "is-focused" : ""
                    }`}
                >
                    <input
                        autoComplete="off"
                        className="quicksearch__input"
                        name={props.parameter}
                        placeholder="Enter search term..."
                        type="text"
                        value={inputValue}
                        onChange={handleChange}
                        onFocus={queueFocus}
                        onBlur={queueBlur}
                    />

                    <button
                        className="quicksearch__button"
                        type="button"
                        onClick={handleClear}
                        onFocus={queueFocus}
                        onBlur={queueBlur}
                    >
                        <i className="icon icon-circle-close quicksearch__icon--clear"></i>
                    </button>
                    <button
                        className="quicksearch__button"
                        type="submit"
                        onFocus={queueFocus}
                        onBlur={queueBlur}
                    >
                        <i className="icon icon-search quicksearch__icon--submit"></i>
                    </button>
                </div>
            </form>
            <div className="quicksearch__results">
                {state.loading && (
                    <div className="quicksearch__result">
                        <QuickSearchResult text={"Loading..."} noLine />
                    </div>
                )}
                {inputValue && !state.loading && isFocused
                    ? results.map((result: Object): React.Node => (
                          <a
                              href={
                                  result.url ||
                                  `${props.action}?${props.parameter}=${result.value}`
                              }
                              key={result.value}
                              className={`quicksearch__result quicksearch__result--${
                                  result.url ? "hit" : "suggestion"
                              }`}
                              onFocus={queueFocus}
                              onBlur={queueBlur}
                          >
                              <QuickSearchResult
                                  text={result.value}
                                  query={inputValue}
                              />
                          </a>
                      ))
                    : null}
            </div>
        </div>
    );
};

export default QuickSearch;
